import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';
import { Button } from 'components/ui/button';
import Skeleton from 'components/ui/skeleton';
import { useToast } from 'components/ui/use-toast';
import { PlanCheckoutModal } from 'components/portal/PlanCheckoutModal';
import { useIsMobile } from 'hooks/use-media-query';
import { cn } from 'lib/utils';
import { setVocabLearned, useVocabulary, type VocabWord } from 'modules/vocabulary';
import { translationOf } from '../words/WordCard';
import { CardBack, CardFront } from './CardFace';
import { Done, EmptyDeck, WORDS_PATH } from './Done';
import {
  answer,
  buildDeck,
  currentWord,
  isFinished,
  progress,
  reveal,
  startSession,
  summary,
  type SessionState,
} from './session';

const focusRing = 'focus-visible:!outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2';

/** "Kartochkalar": a session over the learner's not-yet-learned saved words, 20 at a time. */
export default function PortalFlashcards() {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const { data, isLoading, isError, isFetching } = useVocabulary();
  const [session, setSession] = useState<SessionState | null>(null);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const revealRef = useRef<HTMLButtonElement>(null);
  const backRef = useRef<HTMLDivElement>(null);
  // Focus moves only once the learner has acted, so opening the page does not steal it from the nav.
  const interacted = useRef(false);

  // The deck is taken once, from the list as the server has it now, and then held: marking a word learned must not
  // reshuffle the cards under the learner's hands.
  useEffect(() => {
    if (!session && data && !isFetching) setSession(startSession(buildDeck(data.words)));
  }, [data, isFetching, session]);

  const word = session ? currentWord(session) : undefined;
  const revealed = session?.revealed === true;

  useEffect(() => {
    if (!interacted.current) return;
    (revealed ? backRef.current : revealRef.current)?.focus();
  }, [word?.id, revealed]);

  const flip = useCallback(() => {
    interacted.current = true;
    setSession((current) => (current ? reveal(current) : current));
  }, []);

  const respond = useCallback(
    (knew: boolean) => {
      if (!session?.revealed) return;
      const card = currentWord(session);
      if (!card) return;
      interacted.current = true;
      setSession(answer(session, knew));
      if (knew) {
        setVocabLearned(card.id, true).catch(() => toast({ title: t('vocab.learn_failed'), variant: 'destructive' }));
      }
    },
    [session, toast, t],
  );

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (!session || event.defaultPrevented || event.metaKey || event.ctrlKey || event.altKey) return;
      const target = event.target instanceof Element ? event.target : null;
      if (target?.closest('input, textarea, select, [contenteditable], [role="dialog"]')) return;
      if (!session.revealed) {
        // A focused button or link already turns Space into a click.
        if (event.key === ' ' && !target?.closest('button, a')) {
          event.preventDefault();
          flip();
        }
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        respond(false);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        respond(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [session, flip, respond]);

  const replay = (words: VocabWord[]) => {
    interacted.current = false;
    setSession(startSession(words));
  };

  const back = (
    <Link
      to={WORDS_PATH}
      className={cn('inline-flex min-h-11 items-center gap-1 self-start rounded-lg text-[13px] font-bold text-muted-foreground hover:text-foreground', focusRing)}
    >
      <ArrowLeft className="h-4 w-4" aria-hidden /> {t('vocab.title')}
    </Link>
  );

  let body: React.ReactNode;
  if (isLoading || (data && !session)) {
    body = <Skeleton className="mt-5 h-[380px] w-full rounded-3xl" aria-hidden="true" />;
  } else if (isError || !data || !session) {
    body = <p className="mt-5 text-sm text-destructive">{t('vocab.load_failed')}</p>;
  } else if (session.deck.length === 0) {
    body = <EmptyDeck allLearned={data.words.length > 0} />;
  } else if (isFinished(session)) {
    const { learned, toRepeat } = summary(session);
    body = <Done total={session.deck.length} learned={learned} toRepeat={toRepeat.length} onRepeat={() => replay(toRepeat)} />;
  } else if (word) {
    const { done, total } = progress(session);
    const translation = translationOf(word, i18n.language);
    const locked = data ? !data.access : false;
    body = (
      <>
        <div className="mt-3.5 flex items-center gap-3.5">
          <div
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={total}
            aria-valuenow={done}
            aria-label={t('flashcards.title')}
            className="h-2 flex-1 overflow-hidden rounded-full bg-border"
          >
            <div className="h-full rounded-full bg-foreground transition-[width] motion-reduce:transition-none" style={{ width: `${(done / total) * 100}%` }} />
          </div>
          <b className="whitespace-nowrap text-[13px] text-foreground">{t('flashcards.progress', { done, total })}</b>
        </div>

        <p className="sr-only" aria-live="polite" aria-atomic="true">
          {revealed ? t('flashcards.live_back', { word: word.word, translation }) : t('flashcards.live_front', { word: word.word })}
        </p>

        <div className="mt-5">
          {revealed ? (
            <section ref={backRef} tabIndex={-1} aria-label={t('flashcards.card_back')} className="rounded-3xl focus-visible:!outline-none">
              <CardBack key={`${word.id}-back`} word={word} locked={locked} onUpgrade={() => setUpgradeOpen(true)} />
            </section>
          ) : (
            <section aria-label={t('flashcards.card_front')}>
              <CardFront key={`${word.id}-front`} word={word}>
                {!isMobile && (
                  <Button ref={revealRef} type="button" onClick={flip} className={cn('mt-6 h-12 bg-foreground px-[26px] text-background hover:bg-foreground/90', focusRing)}>
                    {t('flashcards.reveal')}
                  </Button>
                )}
              </CardFront>
            </section>
          )}
        </div>

        {revealed ? (
          <div className="mt-4 flex gap-3">
            <button
              type="button"
              onClick={() => respond(false)}
              className={cn('inline-flex h-14 flex-1 items-center justify-center rounded-[14px] border-[1.5px] border-[#F2B8B5] bg-card text-[15px] font-bold text-[#B42318] hover:bg-[#FEF3F2] dark:border-red-400/40 dark:text-red-300 dark:hover:bg-red-500/10', focusRing)}
            >
              {t('flashcards.dont_know')}
            </button>
            <button
              type="button"
              onClick={() => respond(true)}
              className={cn('inline-flex h-14 flex-1 items-center justify-center rounded-[14px] border border-[#B0DD1E] bg-primary text-[15px] font-bold text-primary-foreground hover:brightness-95', focusRing)}
            >
              {t('flashcards.know')}
            </button>
          </div>
        ) : (
          isMobile && (
            <Button ref={revealRef} type="button" onClick={flip} className={cn('mt-4 h-14 w-full rounded-[14px] bg-foreground text-[15px] text-background hover:bg-foreground/90', focusRing)}>
              {t('flashcards.reveal')}
            </Button>
          )
        )}

        <p className="mt-3.5 text-center text-[12.5px] text-muted-foreground">
          {isMobile ? (
            revealed && t('flashcards.hint_know')
          ) : revealed ? (
            <>
              <kbd className="rounded-md border border-border bg-card px-1.5 py-0.5 font-mono text-[11.5px]">←</kbd> {t('flashcards.dont_know')} ·{' '}
              <kbd className="rounded-md border border-border bg-card px-1.5 py-0.5 font-mono text-[11.5px]">→</kbd> {t('flashcards.know')}. {t('flashcards.hint_know')}
            </>
          ) : (
            <>
              <kbd className="rounded-md border border-border bg-card px-1.5 py-0.5 font-mono text-[11.5px]">{t('flashcards.key_space')}</kbd> {t('flashcards.hint_flip')}
            </>
          )}
        </p>
      </>
    );
  }

  return (
    <div className="mx-auto flex w-full max-w-[760px] flex-col">
      {back}
      {body}
      <PlanCheckoutModal open={upgradeOpen} onOpenChange={setUpgradeOpen} defaultPlan="PRO" />
    </div>
  );
}
